// Outcome naming rules — Polymarket outcomes are player/team names as often as Yes/No.

import { truncate } from '../components/charts/chartFormat';

/** Compact trade badges in the trades list. */
export const OUTCOME_MAXLEN_BADGE = 16;
/** DOM tooltips, which have more room than a badge. */
export const OUTCOME_MAXLEN_TOOLTIP = 24;
/** On-canvas chart labels, which cannot fall back to hover. */
export const OUTCOME_MAXLEN_CANVAS = 12;

/** Pairs where green/red carries meaning, as [first, second] lowercase — order is significant, so a reversed pair degrades to neutral rather than colouring the wrong side. */
const DIRECTIONAL_PAIRS: readonly (readonly [string, string])[] = [
    ['yes', 'no'],
    ['up', 'down'],
    ['odd', 'even'],
];

export interface OutcomeLabel {
    /** Uppercased and truncated — what is drawn. */
    text: string;
    /** Uppercased, untruncated — the title/hover text. */
    title: string;
}

/** Normalized [a, b] for a well-formed two-outcome pair, else null. */
function normalizedPair(
    outcomes: string[] | null | undefined
): [string, string] | null {
    if (!outcomes || outcomes.length !== 2) return null;
    const [a, b] = outcomes;
    if (typeof a !== 'string' || typeof b !== 'string') return null;
    return [a.trim().toLowerCase(), b.trim().toLowerCase()];
}

/** True when `s` is `prefix` as a whole word — "over 2.5" matches, "overton" does not. */
function isWholeToken(s: string, prefix: string): boolean {
    return (
        s === prefix ||
        (s.startsWith(prefix) && !/[a-z]/.test(s[prefix.length] ?? ''))
    );
}

/** The negative half of every directional pair, lowercase. */
const GENERIC_NEGATIVES: readonly string[] = ['no', 'down', 'under', 'even'];

/** True when a single label is a generic negative, so red on it is not a verdict about a named side. */
export function isGenericNegative(label: string | null | undefined): boolean {
    if (typeof label !== 'string') return false;
    const s = label.trim().toLowerCase();
    return GENERIC_NEGATIVES.some(n => isWholeToken(s, n));
}

/** Display forms for the positive half of each directional pair. */
const POSITIVE_OF: Readonly<Record<string, string>> = {
    down: 'Up',
    under: 'Over',
    even: 'Odd',
};

/**
 * The other half of the directional pair `negative` belongs to — "Under" gives
 * "Over". Yes/No is excluded: "Yes" names nothing the caller doesn't already have.
 */
export function directionalCounterpart(
    negative: string | null | undefined
): string | null {
    if (typeof negative !== 'string') return null;
    const trimmed = negative.trim();
    const s = trimmed.toLowerCase();
    for (const [token, positive] of Object.entries(POSITIVE_OF)) {
        // "Under 2.5" gives "Over 2.5" — dropping the threshold would leave the
        // two sides of one market looking like they measure different things.
        if (isWholeToken(s, token)) return positive + trimmed.slice(token.length);
    }
    return null;
}

/** True when the pair is directional, so green/red still means something. */
export function isDirectionalPair(
    outcomes: string[] | null | undefined
): boolean {
    const pair = normalizedPair(outcomes);
    if (!pair) return false;
    if (DIRECTIONAL_PAIRS.some(([a, b]) => pair[0] === a && pair[1] === b))
        return true;
    return isWholeToken(pair[0], 'over') && isWholeToken(pair[1], 'under');
}

/** True when the second outcome should drop red — only when both sides really render as names, so colour never diverges from the YES/NO text fallback. */
export function isNeutralNoSide(
    outcomes: string[] | null | undefined
): boolean {
    if (isDirectionalPair(outcomes)) return false;
    return (
        outcomeLabel(outcomes, 0, Infinity) !== null &&
        outcomeLabel(outcomes, 1, Infinity) !== null
    );
}

/** The named outcome at `index`, or null when the caller should fall back. */
export function outcomeLabel(
    outcomes: string[] | null | undefined,
    index: number | null,
    maxLen: number
): OutcomeLabel | null {
    if (index !== 0 && index !== 1) return null;
    if (!outcomes || outcomes.length !== 2) return null;
    const name = outcomes[index];
    if (typeof name !== 'string') return null;
    const title = name.trim().toUpperCase();
    if (title === '') return null;
    return { text: truncate(title, maxLen), title };
}
